import { renderMarkdownLite } from '@/lib/markdown';
import { type CSSProperties, useEffect, useRef } from 'react';
import { useEditorMode } from './EditorContext';

interface EditableTextProps {
  /** Raw text (may contain markdown-lite `**bold**`). */
  value: string;
  /** Called when the user commits a change (blur or Enter on single-line). */
  onCommit: (next: string) => void;
  /** Multi-line: Enter inserts a newline; commit on blur. Default false. */
  multiline?: boolean;
  /** If true, accent-color the **bold** segments when rendering read-only. */
  boldColor?: string;
  className?: string;
  style?: CSSProperties;
  /** Optional ARIA label for screen readers when contenteditable. */
  ariaLabel?: string;
  /** When true, render plain text only (skip markdown-lite). */
  plain?: boolean;
}

/**
 * A text node that becomes contenteditable when EditorContext.mode === 'edit'.
 * Uncontrolled while editing — the DOM is the source of truth; on blur we
 * read textContent and commit. The component re-syncs from props after
 * commit so external state updates flow back in.
 *
 * Rendered as a `<span>` so it composes anywhere an inline element fits.
 */
export function EditableText({
  value,
  onCommit,
  multiline = false,
  boldColor,
  className,
  style,
  ariaLabel,
  plain = false,
}: EditableTextProps) {
  const mode = useEditorMode();
  const ref = useRef<HTMLSpanElement>(null);

  // Keep the DOM in sync with the value prop when NOT focused.
  // This handles undo/redo and external updates without clobbering active typing.
  useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement === ref.current) return;
    if (ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, [value]);

  if (mode === 'read') {
    return (
      <span className={className} style={style}>
        {plain ? value : renderMarkdownLite(value, boldColor ? { boldColor } : {})}
      </span>
    );
  }

  // Edit mode
  return (
    <span
      ref={ref}
      className={[className ?? '', 'pharma-editable', multiline ? 'whitespace-pre-wrap' : '']
        .filter(Boolean)
        .join(' ')}
      style={style}
      contentEditable
      suppressContentEditableWarning
      aria-label={ariaLabel}
      // biome-ignore lint/a11y/useSemanticElements: contenteditable span can't be a native <input>
      role="textbox"
      tabIndex={0}
      data-multiline={multiline ? 'true' : 'false'}
      onBlur={(e) => {
        const text = e.currentTarget.textContent ?? '';
        if (text !== value) onCommit(text);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          if (ref.current) ref.current.textContent = value;
          e.currentTarget.blur();
        }
        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    >
      {value}
    </span>
  );
}
